import React, {useEffect} from "react";
import {useDispatch} from "react-redux";
import {useForm} from "react-hook-form";
import {
    AddressInput,
    CheckPassword, CreateCancelButtons,
    DateCalendarForm,
    InputMain,
    MaskInput,
    PhoneInput2, SelectMain,
} from "components";
import {
    EmailValidator,
    FindError,
    FindLoad,
    FindSuccess,
    isEmptyObj,
    newDateWithoutUtc,
    PasswordValidator,
    useModal,
} from "utils";
import {usersActions, httpRequestsOnSuccessActions, httpRequestsOnErrorsActions} from "store";
import {checkPassword} from "../../../fragments/auth/constants";
import {Svg} from "assets";
import {AccessLevelEnum, AccessLevelList} from "../constants";

export const AddUser = ({info, afterSuccess}) => {
    const dispatch = useDispatch();
    const {
        handleSubmit,
        control,
        getValues,
        setValue,
        watch,
        setError,
        reset,
        clearErrors,
    } = useForm({
        defaultValues: {
            accessLevel: AccessLevelEnum.MEMBER
        }
    });
    const {close} = useModal();
    const ACTION_TYPE = info ? "EDIT_USERS" : "ADD_USERS";
    const loading = FindLoad(ACTION_TYPE);
    const success = FindSuccess(ACTION_TYPE);
    const beckError = FindError(ACTION_TYPE);

    useEffect(() => {
        if (success?.type) {
            dispatch(httpRequestsOnSuccessActions.removeSuccess(ACTION_TYPE));
            close();
            afterSuccess()
        }
    }, [success]);

    useEffect(() => {
        if (info) {
            reset({
                ...info,
            });
        }
    }, [info]);

    useEffect(() => {
        if (beckError?.error?.includes("phone must be a valid phone number")) {
            setError("phone", {
                message: "Phone must be a valid phone number",
            });
        }

        if (beckError?.error?.includes("Social security number is incorrect")) {
            setError("ssn", {
                message: "Social security number is incorrect",
            });
        }

        if (beckError?.error?.includes("User with this email already exists")) {
            setError("email", {
                message: "User with this email already exists",
            });
        }


    }, [beckError?.error]);

    useEffect(() => {
        dispatch(httpRequestsOnErrorsActions.removeError(ACTION_TYPE));
        clearErrors();

        return () => {
            dispatch(httpRequestsOnErrorsActions.removeError(ACTION_TYPE));
            clearErrors();
        };
    }, []);


    const onSubmit = (data) => {
        const params = {
            ...data,
        };

        if (data?.type) {
            params.type = data?.type.id;
        }

        data?.dob ? (params.dob = newDateWithoutUtc(data?.dob)) : delete params.dob;
        data?.dl ? (params.dl = data?.dl) : delete params.dl;
        data?.ssn ? (params.ssn = data?.ssn) : delete params.ssn;
        data?.position
            ? (params.position = data?.position)
            : delete params.position;
        if (data?.password) delete params?.confirmation;

        if (isEmptyObj(params?.address)) {
            delete params?.address;
        }

        if (info) {
            dispatch(usersActions.editUser(info?.id, params));
        } else {
            dispatch(usersActions.addUser(params));
        }
    };

    return (
        <form className="add-user-modal-wrapper" onSubmit={handleSubmit(onSubmit)}>
            <div className="modal-header-main">
                <h3 className="modal-header-title">
                    {info ? "Edit" : "Add"} Member
                </h3>

                <button className="modal-close-btn-main" type="button" onClick={close}>
                    <Svg.CloseSvg/>
                </button>
            </div>

            <div className="modal-body-main">
                <div className="flex-gap-16">
                    <div className="full-width">
                        <div className="flex-gap-16">
                            <InputMain
                                name="firstName"
                                placeholder="Ex. Sarah"
                                control={control}
                                labelHeader="First Name"
                                rules={{
                                    required: "First Name is required",
                                }}
                            />

                            <InputMain
                                name="lastName"
                                placeholder="Ex. Smith"
                                control={control}
                                labelHeader="Last Name"
                                rules={{
                                    required: "Name is required",
                                }}
                            />
                        </div>

                        <SelectMain
                            list={AccessLevelList}
                            renderLabel={(option) => option?.name}
                            disableClearable
                            name="accessLevel"
                            control={control}
                            labelHeader='Access Level'
                            placeholder="Select Option"
                            selectedValue='id'
                            rules={{
                                required: "Access Level is required",
                            }}
                        />

                        <DateCalendarForm name="dob" control={control} label="DOB"/>

                        <PhoneInput2
                            label="Phone Number*"
                            placeholder={"ex (000) 000-0000"}
                            name="phone"
                            control={control}
                            rules={{
                                required: "Phone Number is required",
                                minLength: {
                                    value: 10,
                                    message: "Please enter a valid 10 digit number.",
                                },
                                validate: (value) => {
                                    return value
                                        ? value?.replace(/[^0-9]/g, "").length === 10 ||
                                        "Please enter a valid phone number."
                                        : true;
                                },
                            }}
                        />

                        <InputMain
                            name="email"
                            placeholder="Ex. example@gmail.com"
                            type="email"
                            control={control}
                            labelHeader="Email"
                            rules={{
                                required: "Email is required",
                                pattern: {
                                    value: EmailValidator,
                                    message: "Must be valid email",
                                },
                            }}
                        />
                        <div className='flex-gap-16'>
                            <MaskInput
                                labelHeader="SSN"
                                mask="999-99-9999"
                                name="ssn"
                                control={control}
                                placeholder='Ex. 123-45-6789'
                            />
                            <InputMain
                                name="dl"
                                placeholder="Ex. D123-4567-8901"
                                control={control}
                                labelHeader="DI#"
                            />
                        </div>
                    </div>

                    <div className="full-width">
                        <InputMain
                            name="position"
                            placeholder="Ex. Super Amdin"
                            control={control}
                            labelHeader="Position"
                        />

                        <AddressInput
                            control={control}
                            allValues={getValues()?.address}
                            setValue={setValue}
                        />
                    </div>
                </div>

                {!info && (
                    <div className="add-user-modal-security-wrapper">
                        <h4 className="dd-user-modal-security-title">Security</h4>
                        <div className="flex-gap-16">
                            <div className="full-width">
                                <InputMain
                                    variant="outlined"
                                    name="password"
                                    type="password"
                                    control={control}
                                    placeholder={"Create Password"}
                                    labelHeader={"Create Password"}
                                    minLength={8}
                                    rules={{
                                        required: "New password is required.",
                                        pattern: {
                                            value: PasswordValidator,
                                            message: "Not valid password",
                                        },
                                    }}
                                    startAdornment={<Svg.LockPassSvg/>}
                                />
                                <CheckPassword
                                    fields={checkPassword}
                                    text={watch("password")}
                                    isActive={watch("password")}
                                />
                            </div>

                            <InputMain
                                variant="outlined"
                                name="confirmation"
                                type="password"
                                control={control}
                                placeholder={"Confirm Password"}
                                labelHeader={"Confirm Password"}
                                minLength={8}
                                rules={{
                                    required: "Confirm password is required.",
                                    validate: (value) => {
                                        return value === watch("password") || 'Password and confirm password does not match'
                                    }
                                }}
                                startAdornment={<Svg.LockPassSvg/>}
                            />
                        </div>
                    </div>
                )}
            </div>

            <CreateCancelButtons
                createText={info ? "Save" : "Add"}
                loading={!!loading?.length}
            />
        </form>
    );
};
